import React from 'react';
import type { CustomCellRendererProps } from 'ag-grid-react';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SYMBOL_FOR_EMPTY_CELL } from 'components/AgGrid/helpers';

/**
 * Display renderer for the editable vendor-level columns (Withholding Tax / Valid Until).
 * While the row's PATCH is in flight it shows a spinner instead of the value, so the user
 * sees the inline edit is being saved. Updating state is read from the grid `context`.
 *
 * Editable cells render a muted pencil affordance (brightening on hover) so it's obvious at a
 * glance which cells can be changed. Editability is taken from AG Grid itself
 * (`column.isCellEditable`) so it stays correct regardless of how the colDef defines `editable`.
 */
const VendorFieldCellRenderer: React.FC<CustomCellRendererProps> = ({
  valueFormatted,
  value,
  data,
  colDef,
  context,
  column,
  node,
}) => {
  const field = (colDef as { field?: string })?.field;
  const vendorId = data?.vendorId as string | undefined;
  const isUpdating = context?.isCellUpdating?.(vendorId, field) ?? false;
  const isEditable = column?.isCellEditable(node) ?? false;

  if (isUpdating) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ height: '100%' }}>
        <CircularProgress size={16} />
      </Stack>
    );
  }

  const display = valueFormatted ?? (value != null && value !== '' ? value : SYMBOL_FOR_EMPTY_CELL);

  if (!isEditable) return <>{display}</>;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={0.5}
      sx={{
        height: '100%',
        '& .editable-pencil': {
          opacity: 0.4,
          transition: 'opacity 0.15s ease, color 0.15s ease',
          pointerEvents: 'none',
        },
        '&:hover .editable-pencil': { opacity: 1, color: 'primary.main' },
      }}
    >
      <span>{display}</span>
      <EditOutlinedIcon className="editable-pencil" sx={{ fontSize: 14 }} />
    </Stack>
  );
};

export default VendorFieldCellRenderer;
